import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { TemplateFormDialogContentsSectionForm } from "./types";
import { Button, Label } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useCallback, useId, useRef, useState } from "react";
import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableProductRow } from "./SortableProductRow";

export interface ProductsSectionProps {
  form: TemplateFormDialogContentsSectionForm<"productIds">;
}

export const ProductsSection = ({ form }: ProductsSectionProps) => {
  const { t } = useTranslation("calendar");
  const productsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });

  const baseId = useId();
  const nextKeyRef = useRef(0);
  const generateRowKey = useCallback(() => `${baseId}-${nextKeyRef.current++}`, [baseId]);

  const [rowKeys, setRowKeys] = useState<string[]>(() => form.values.productIds.map(generateRowKey));

  const productIds = form.values.productIds;
  const rows = rowKeys.map((rowKey, i) => ({ rowKey, productId: productIds[i] ?? "" }));

  const sensors = useSensors(useSensor(PointerSensor));

  const handleProductChange = useCallback(
    (newId: string, index: number) => {
      const shouldDelete = !newId && productIds.length > 1;
      if (shouldDelete) {
        form.setFieldValue("productIds", [...productIds.slice(0, index), ...productIds.slice(index + 1)]);
        setRowKeys((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
        return;
      }

      form.setFieldValue("productIds", [...productIds.slice(0, index), newId, ...productIds.slice(index + 1)]);
    },
    [form, productIds]
  );

  const handleAddProduct = useCallback(() => {
    form.setFieldValue("productIds", [...productIds, ""]);
    setRowKeys((prev) => [...prev, generateRowKey()]);
  }, [form, productIds, generateRowKey]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = rowKeys.indexOf(String(active.id));
      const newIndex = rowKeys.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      form.setFieldValue("productIds", arrayMove(productIds, oldIndex, newIndex));
      setRowKeys((prev) => arrayMove(prev, oldIndex, newIndex));
    },
    [form, productIds, rowKeys]
  );

  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Associated Products")}</Label>
        <p className="text-xs text-muted-foreground font-light">
          {t("Products will be available for purchase during the booking process. Drag to reorder.")}
        </p>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {rows.map(({ rowKey, productId }, i) => (
              <SortableProductRow
                key={rowKey}
                rowKey={rowKey}
                productId={productId}
                options={productsQuery.data ?? []}
                disableRemove={i === 0 && !productId}
                onChange={(newId) => handleProductChange(newId, i)}
                onRemove={() => handleProductChange("", i)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button variant="outline" size="sm" className="font-light text-xs h-auto py-1" onClick={handleAddProduct}>
        <Plus />
        {t("Add Product")}
      </Button>
    </section>
  );
};
